import React, { useEffect, useMemo } from 'react';

import { Controller, UseFormReturn } from 'react-hook-form';

import { useDebounce } from 'hooks/useDebaunce';

import { useVersionStore } from 'store/version.store';

import { useQueryGetMilestoneFragnet } from 'api/queries/getMilestoneFragnet.query';

import classes from 'components/common/GlobalFiltersInPage/Filters.module.scss';
import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';
import { CustomizedFormErrorMessage } from 'components/common/index';

import { Select } from 'components/DesignSystem/Select';

interface Props {
    methods?: UseFormReturn<any, any>;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    versionId?: number | undefined;
    value?: string;
    isSelected?: boolean;
}

export const ActivityForwardFragnetFilter = React.forwardRef(
    ({ methods, name, filtersStatusRef, handleFilters, value, isSelected = true, versionId }: Props) => {
        const [filter, setFilter] = React.useState<string>('');
        const debouncedValue = useDebounce<string>(filter, 1000);
        const {
            data: milestoneFragnet,
            isFetching,
            isSuccess,
        } = useQueryGetMilestoneFragnet({
            latestVersion: versionId,
            filter: debouncedValue,
        });

        const handleInputChangePrimary = (inputText, event) => {
            if (event.action !== 'input-blur' && event.action !== 'menu-close' && event.action !== 'set-value') {
                setFilter(inputText);
            }
            if (event.action === 'menu-close') {
                setFilter('');
            }
        };

        const options = useMemo<ISelectOption<string>[]>(
            () =>
                milestoneFragnet
                    ? milestoneFragnet.map((item) => ({
                          id: item.id,
                          label: `${item.name} (ID: ${
                              item.activity_id.length > 20 ? item.activity_id.slice(0, 20) + '...' : item.activity_id
                          }) (~${item.succeding_activities} related activities)`,
                          value: item.task_version_hash_code,
                      }))
                    : [],
            [milestoneFragnet],
        );

        useEffect(() => {
            if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady || !isSuccess) {
                return;
            }

            signalSingleFilterIsReady({ filterId: name, isReady: isSuccess });
        }, [value, milestoneFragnet, filtersStatusRef, isSuccess, name, methods]);

        return isSelected ? (
            <>
                <div data-testid={'battlecards-filters-activity-forward-fragnet'} className={classes.selectWrapper}>
                    <Controller
                        render={({ field }) => (
                            <Select<ISelectOption<string>>
                                id={'battlecards-filters-activity-forward-fragnet'}
                                value={options.find((option) => option.value === value)}
                                options={options}
                                onChange={async (newValue) => {
                                    methods?.setValue(name, newValue ? newValue.value : null, { shouldValidate: true });
                                    const isValid = await methods?.trigger(name);
                                    if (isValid) {
                                        handleFilters && handleFilters();
                                    }
                                }}
                                isClearable={true}
                                isLoading={isFetching}
                                isError={Boolean(methods?.formState?.errors[name])}
                                onInputChange={handleInputChangePrimary}
                                label={FilterLabels.activityForwardFragnet}
                            />
                        )}
                        name={name}
                        control={methods?.control}
                        rules={{
                            validate: {
                                activityFragnetValidation: (value) => {
                                    return (
                                        !Boolean(methods?.watch('milestoneFragnet') && value) ||
                                        'You can only select one filter at a time: either "Activity Fragnet" or "Activity Forward Fragnet."'
                                    );
                                },
                            },
                        }}
                    />
                </div>
                {methods?.formState?.errors?.[name] && (
                    <CustomizedFormErrorMessage text={methods?.formState?.errors?.[name]?.message} />
                )}
            </>
        ) : null;
    },
);
